import React, { useMemo, useRef, useState, useLayoutEffect, useCallback } from "react";
import { Tooltip } from "src/assets/imports/antdComponents";
import WaveSelectorModal from "./LegendSelectorModal";

interface IHorizontalLegendProps {
	waves: Record<string, string>;
	visibleWaves: string[];
	setVisibleWaves: React.Dispatch<React.SetStateAction<string[]>> | React.Dispatch<React.SetStateAction<string[] | undefined>>;
	expanded: boolean;
	setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
	waveMarkers?: Record<string, string>;
	displayLines?: number;
	/**
	 * Font size in px for legend item labels.
	 * Defaults to 11 — identical to the previous hardcoded text-[11px].
	 * Pass scaled.typography.legend from useScaledConfig() in multi-tile mode.
	 */
	fontSize?: string | number;
	/** Explicit display order. When provided, overrides JS object-key enumeration of `waves`. */
	order?: (string | number)[];
}

const MAX_LABEL_WIDTH = 200;
const SHOW_ALL_MIN_WIDTH = 80;
const CONTAINER_PADDING = 16;

// Reorders legend entries by an explicit `order` list. Entries not present in
// `order` keep their original relative position at the end (Array.sort is stable).
const sortByOrder = <T extends { key: string }>(entries: T[], order: (string | number)[]): T[] => {
	const rank = new Map(order.map((key, index) => [String(key), index]));
	return entries.slice().sort((a, b) => {
		const ra = rank.has(a.key) ? (rank.get(a.key) as number) : Number.MAX_SAFE_INTEGER;
		const rb = rank.has(b.key) ? (rank.get(b.key) as number) : Number.MAX_SAFE_INTEGER;
		return ra - rb;
	});
};

// ─── Marker size derived from font size ──────────────────────────────────────
// At fontSize=11 (default) → markerSize=10, matching the old hardcoded value.
// Scales linearly with font size so marker and label stay visually balanced.
const getMarkerSize = (fontSize: number): number => Math.max(6, Math.round(fontSize * (10 / 11)));

/* -------------------- Marker Symbol Component -------------------- */
const MarkerSymbol: React.FC<{
	markerType: string;
	color: string;
	size?: number;
	active?: boolean;
}> = ({ markerType, color, size = 10, active = true }) => {
	const opacity = active ? 1 : 0.3;
	const halfSize = size / 2;

	switch (markerType) {
		case "Square":
			return (
				<svg width={size} height={size} className="flex-shrink-0">
					<rect x="1" y="1" width={size - 2} height={size - 2} fill={color} opacity={opacity} />
				</svg>
			);
		case "Triangle":
			return (
				<svg width={size} height={size} className="flex-shrink-0">
					<polygon points={`${halfSize},1 ${size - 1},${size - 1} 1,${size - 1}`} fill={color} opacity={opacity} />
				</svg>
			);
		case "Diamond":
			return (
				<svg width={size} height={size} className="flex-shrink-0">
					<polygon points={`${halfSize},1 ${size - 1},${halfSize} ${halfSize},${size - 1} 1,${halfSize}`} fill={color} opacity={opacity} />
				</svg>
			);
		case "Cross":
			return (
				<svg width={size} height={size} className="flex-shrink-0">
					<line x1="2" y1="2" x2={size - 2} y2={size - 2} stroke={color} strokeWidth="2" opacity={opacity} />
					<line x1={size - 2} y1="2" x2="2" y2={size - 2} stroke={color} strokeWidth="2" opacity={opacity} />
				</svg>
			);
		case "Pin":
			return (
				<svg width={size} height={size} className="flex-shrink-0">
					<circle cx={halfSize} cy={halfSize - 1} r={halfSize - 2} fill={color} opacity={opacity} />
					<polygon points={`${halfSize},${size - 1} ${halfSize - 2},${halfSize} ${halfSize + 2},${halfSize}`} fill={color} opacity={opacity} />
				</svg>
			);
		case "Arrow":
			return (
				<svg width={size} height={size} className="flex-shrink-0">
					<polygon points={`${halfSize},1 ${size - 1},${size - 1} ${halfSize},${size - 3} 1,${size - 1}`} fill={color} opacity={opacity} />
				</svg>
			);
		case "Circle":
		default:
			return <span className="flex-shrink-0 rounded-full" style={{ width: size, height: size, backgroundColor: color, opacity }} />;
	}
};

// ─── Width estimator — must use the same fontSize as rendering ───────────────
const getEstimatedTextWidth = (text: string, fontSize: number, markerSize: number, maxWidth: number = MAX_LABEL_WIDTH): number => {
	const avgCharWidth = fontSize * 0.6;
	const textWidth = Math.min(text.length * avgCharWidth, maxWidth);
	// marker(markerSize) + gap between marker and label(6px) + gap between items(16px)
	return textWidth + markerSize + 6 + 16;
};

/* -------------------- LegendItem -------------------- */
const LegendItem: React.FC<{
	wave: { key: string; label: string; color: string };
	markerType?: string;
	active: boolean;
	onToggle: () => void;
	maxLabelWidth: number;
	fontSize: number; // ← new
	markerSize: number; // ← new
}> = ({ wave, markerType, active, onToggle, maxLabelWidth, fontSize, markerSize }) => {
	const labelRef = useRef<HTMLSpanElement>(null);
	const [isTruncated, setIsTruncated] = useState(false);

	useLayoutEffect(() => {
		const checkTruncation = () => {
			if (labelRef.current) {
				setIsTruncated(labelRef.current.scrollWidth > labelRef.current.clientWidth);
			}
		};
		checkTruncation();
		const ro = new ResizeObserver(checkTruncation);
		if (labelRef.current) ro.observe(labelRef.current);
		return () => ro.disconnect();
	}, [wave.label, fontSize]); // ← re-check when fontSize changes

	const labelContent = (
		<span
			ref={labelRef}
			className={`leading-snug truncate ${active ? "text-[#403C45]" : "text-gray-400"}`}
			style={{
				// Inline style — never a dynamic Tailwind class (JIT won't purge-generate it)
				fontSize,
				maxWidth: `${maxLabelWidth}px`,
			}}>
			{wave.label}
		</span>
	);

	return (
		<div onClick={onToggle} className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0">
			<MarkerSymbol
				markerType={markerType || "Circle"}
				color={wave.color}
				size={markerSize} // ← scaled
				active={active}
			/>
			{isTruncated ? (
				<Tooltip title={wave.label} placement="top">
					{labelContent}
				</Tooltip>
			) : (
				labelContent
			)}
		</div>
	);
};

/* -------------------- HorizontalLegend -------------------- */
const HorizontalLegend: React.FC<IHorizontalLegendProps> = ({
	waves,
	visibleWaves,
	setVisibleWaves,
	expanded,
	setExpanded,
	waveMarkers,
	displayLines = 2,
	fontSize = 11, // ← default = old hardcoded value, fully backward compatible
	order,
}) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const showAllRef = useRef<HTMLButtonElement>(null);
	const [isCalculating, setIsCalculating] = useState(true);
	const [visibleCount, setVisibleCount] = useState<number | null>(null);

	// Derived from fontSize so estimation always matches rendering
	const markerSize = useMemo(() => getMarkerSize(fontSize), [fontSize]);
	const maxLabelWidth = MAX_LABEL_WIDTH;

	const waveEntries = useMemo(() => {
		const entries = Object.entries(waves).map(([label, color]) => ({ key: label, label, color }));
		return order && order.length > 0 ? sortByOrder(entries, order) : entries;
	}, [waves, order]);

	const calculateVisibleItems = useCallback(() => {
		if (!containerRef.current || waveEntries.length === 0) return;
		const containerWidth = containerRef.current.offsetWidth - CONTAINER_PADDING;
		if (containerWidth <= 0) return;

		const showAllWidth = SHOW_ALL_MIN_WIDTH + 16;
		const gap = 16;

		// Use the same fontSize + markerSize as actual rendering
		const itemWidths = waveEntries.map((wave) => getEstimatedTextWidth(wave.label, fontSize, markerSize, maxLabelWidth) + gap);

		let count = 0;

		if (displayLines === 1) {
			let currentWidth = 0;
			for (let i = 0; i < itemWidths.length; i++) {
				const isLast = i === itemWidths.length - 1;
				const wouldFit = currentWidth + itemWidths[i] <= containerWidth;
				const wouldFitWithShowAll = currentWidth + itemWidths[i] + showAllWidth <= containerWidth;
				if (isLast && wouldFit) {
					count++;
				} else if (wouldFitWithShowAll) {
					currentWidth += itemWidths[i];
					count++;
				} else {
					break;
				}
			}
		} else {
			const availableWidthPerLine = containerWidth;
			let currentLineWidth = 0;
			let currentLine = 1;

			for (let i = 0; i < itemWidths.length; i++) {
				const itemWidth = itemWidths[i];
				const isLast = i === itemWidths.length - 1;
				const isOnLastAllowedLine = currentLine === displayLines;
				const reservedSpace = isOnLastAllowedLine && !isLast ? showAllWidth : 0;
				const effectiveLineWidth = availableWidthPerLine - reservedSpace;

				if (currentLineWidth + itemWidth <= effectiveLineWidth) {
					currentLineWidth += itemWidth;
					count++;
				} else if (!isOnLastAllowedLine) {
					currentLine++;
					currentLineWidth = itemWidth;
					count++;
				} else {
					break;
				}
			}
		}

		setVisibleCount((prev) => (prev === count ? prev : count));
		setIsCalculating(false);
	}, [waveEntries, displayLines, fontSize, markerSize, maxLabelWidth]);
	// ↑ fontSize + markerSize in deps — recalculate when scale changes

	useLayoutEffect(() => {
		calculateVisibleItems();
		let resizeTimeout: NodeJS.Timeout;
		const ro = new ResizeObserver(() => {
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(calculateVisibleItems, 50);
		});
		const target = containerRef.current?.parentElement ?? containerRef.current;
		if (target) ro.observe(target);
		return () => {
			clearTimeout(resizeTimeout);
			ro.disconnect();
		};
	}, [calculateVisibleItems]);

	const { hasMore, hiddenCount } = useMemo(() => {
		const hasMore = visibleCount !== null && waveEntries.length > visibleCount;
		const hiddenCount = visibleCount !== null ? waveEntries.length - visibleCount : 0;
		return { hasMore, hiddenCount };
	}, [visibleCount, waveEntries.length]);

	const toggleWave = (key: string) => {
		(setVisibleWaves as React.Dispatch<React.SetStateAction<string[]>>)((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
	};

	if (!visibleWaves) return null;

	return (
		<div ref={containerRef} className="w-full overflow-hidden">
			<div className={`flex items-center justify-center gap-x-4 gap-y-1 ${displayLines > 1 ? "flex-wrap" : "flex-nowrap"}`}>
				{waveEntries.map((wave, index) => {
					const active = visibleWaves.includes(wave.key);
					const isVisible = visibleCount === null || index < visibleCount;
					if (!isVisible) return null;

					return (
						<LegendItem
							key={wave.key}
							wave={wave}
							markerType={waveMarkers?.[wave.key]}
							active={active}
							onToggle={() => toggleWave(wave.key)}
							maxLabelWidth={maxLabelWidth}
							fontSize={fontSize} // ← passed through
							markerSize={markerSize} // ← passed through
						/>
					);
				})}

				<button
					ref={showAllRef}
					onClick={() => setExpanded(true)}
					className={`flex items-center font-medium text-[--primary-color-2] hover:underline whitespace-nowrap flex-shrink-0 ${hasMore ? "" : "hidden"}`}
					style={{ fontSize }} // ← "+N More" button matches label size
				>
					+{hiddenCount} More
				</button>
			</div>

			{expanded && (
				<WaveSelectorModal waves={waves} visibleWaves={visibleWaves} setVisibleWaves={setVisibleWaves} onClose={() => setExpanded(false)} waveMarkers={waveMarkers} />
			)}
		</div>
	);
};

export default HorizontalLegend;
